"use client";

import { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { User } from '@supabase/supabase-js';
import { createClient } from '@/utils/supabase/client';

interface UserProfile {
  id: string;
  user_type: 'tenant' | 'landlord';
  name?: string;
  email: string;
  country?: string;
  avatar_url?: string;
}

interface AuthState {
  user: User | null;
  profile: UserProfile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  userType: 'tenant' | 'landlord' | null;
  isSessionExpired: boolean;
}

interface AuthContextType extends AuthState {
  refreshAuth: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  profile: null,
  isLoading: true,
  isAuthenticated: false,
  userType: null,
  isSessionExpired: false,
  refreshAuth: async () => {},
  signOut: async () => {}
});

export const useAuth = () => useContext(AuthContext);

interface AuthStateHandlerProps {
  children: ReactNode;
  initialAuth?: {
    isAuthenticated: boolean;
    userType: 'tenant' | 'landlord' | null;
    userId?: string;
    userEmail?: string;
  };
}

export default function AuthStateHandler({ 
  children,
  initialAuth
}: AuthStateHandlerProps) {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    profile: null,
    isLoading: !initialAuth,
    isAuthenticated: initialAuth?.isAuthenticated || false,
    userType: initialAuth?.userType || null,
    isSessionExpired: false
  });

  const supabase = createClient();

  const fetchUserProfile = async (user: User): Promise<UserProfile | null> => {
    try {
      const { data: profile, error } = await supabase
        .from('tenant_profiles')
        .select('id, user_type, name, email, country, avatar_url')
        .eq('id', user.id)
        .single();

      if (error) {
        // Try profiles table as fallback
        const { data: fallbackProfile, error: fallbackError } = await supabase
          .from('profiles')
          .select('id, user_type, name, email, country, avatar_url')
          .eq('id', user.id)
          .single();

        if (fallbackError) {
          console.error('Failed to fetch user profile:', fallbackError);
          return null;
        }

        return fallbackProfile as UserProfile;
      }

      return profile as UserProfile;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  };

  const updateAuthState = async (user: User | null) => {
    if (!user) {
      setAuthState({
        user: null,
        profile: null,
        isLoading: false,
        isAuthenticated: false,
        userType: null,
        isSessionExpired: false
      });
      return;
    }

    const profile = await fetchUserProfile(user);
    
    setAuthState({
      user,
      profile,
      isLoading: false,
      isAuthenticated: true,
      userType: profile?.user_type || 'tenant',
      isSessionExpired: false
    });
  };

  const refreshAuth = async () => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));
      
      const { data: { user }, error } = await supabase.auth.getUser();
      
      if (error) {
        console.error('Auth refresh error:', error);
        setAuthState(prev => ({ 
          ...prev, 
          isLoading: false,
          isSessionExpired: error.message.includes('expired') || error.message.includes('invalid')
        }));
        return;
      }

      await updateAuthState(user);
    } catch (error) {
      console.error('Unexpected auth refresh error:', error);
      setAuthState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const signOut = async () => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));
      
      await supabase.auth.signOut({ scope: 'global' });
      
      // Clear local storage
      if (typeof window !== 'undefined') {
        ['rentwise-user-preferences', 'rentwise-theme-preference', 'rentwise-dashboard-layout']
          .forEach(key => localStorage.removeItem(key));
      }

      setAuthState({
        user: null,
        profile: null,
        isLoading: false,
        isAuthenticated: false,
        userType: null,
        isSessionExpired: false
      });

      // Redirect to login
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
    } catch (error) {
      console.error('Sign out error:', error);
      setAuthState(prev => ({ ...prev, isLoading: false }));
    }
  };

  useEffect(() => {
    // Initialize with SSR data if available
    if (initialAuth?.isAuthenticated && initialAuth.userId) {
      const initializeUser = async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await updateAuthState(user);
        } else {
          setAuthState(prev => ({ ...prev, isLoading: false }));
        }
      };
      initializeUser();
    } else {
      // No initial auth, check current state
      refreshAuth();
    }

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event);
        
        if (event === 'SIGNED_OUT' || !session?.user) {
          setAuthState({
            user: null,
            profile: null,
            isLoading: false,
            isAuthenticated: false,
            userType: null,
            isSessionExpired: false
          });
        } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          await updateAuthState(session.user);
        } else if (event === 'USER_UPDATED') {
          if (session?.user) {
            await updateAuthState(session.user);
          }
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [initialAuth]);

  // Handle session expiry
  useEffect(() => {
    if (authState.isSessionExpired && typeof window !== 'undefined') {
      const timeoutId = setTimeout(() => {
        window.location.href = '/login?message=session_expired';
      }, 3000);

      return () => clearTimeout(timeoutId);
    }
  }, [authState.isSessionExpired]);

  const contextValue: AuthContextType = {
    ...authState,
    refreshAuth,
    signOut
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
} 